import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import { translateText } from "../../utils/translation";
import { getAnalytics } from "../../slices/insights";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import LineChart from "../charts/line/LineChart";
import DonutChart from "../charts/radial/DonutChart";
import RadialChart from "../charts/radial/RadialChart";
import ActionButtons from "../layout/ActionButtons";
import ButtonDropdown from "../forms/ButtonDropdown";
import { Menu } from "@headlessui/react";
import { CSVLink } from "react-csv";

export default function Analytics() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getAnalytics({ dateRange: dateRangeTerm }));
  }, [dispatch, dateRangeTerm]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { analytics, options } = useAppSelector((state) => state.insights);
  const language = user === null ? "EN" : user.language;

  const analyticsData = analytics.data;
  const requestsByHour =
    analyticsData === null
      ? [{ id: "timeSeries", data: [] }]
      : [
          {
            id: "timeSeries",
            data: analyticsData.request_time_series.map((x) => ({
              x: x.hour,
              y: x.count,
            })),
          },
        ];
  const categoryFrequency =
    analyticsData === null
      ? []
      : analyticsData?.request_category.map((category) => ({
          id: options.requests[category.category][language],
          label: options.requests[category.category][language],
          value: category.count,
        }));
  const roomFrequency =
    analyticsData === null
      ? []
      : analyticsData?.request_rooms.map((room) => ({
          id: room.room_name,
          label: room.room_name,
          value: room.count,
        }));

  const orangeLimit = user === null ? 5 : user.property.orange_limit;
  const redLimit = user === null ? 10 : user.property.red_limit;
  const responseTime =
    analyticsData === null
      ? []
      : [
          {
            id: translateText("orange_limit", language),
            data: [
              { x: translateText("lower_limit", language), y: orangeLimit },
            ],
          },
          {
            id: translateText("average_response_time", language),
            data: [
              {
                x: translateText("average", language),
                y: analyticsData?.response_time.average,
              },
            ],
          },
          {
            id: translateText("red_limit", language),
            data: [{ x: translateText("upper_limit", language), y: redLimit }],
          },
        ];
  const highlightedValue = analyticsData?.response_time.average;
  let highlightedColour = "#4adf9f";
  if (highlightedValue < redLimit && highlightedValue > orangeLimit) {
    highlightedColour = "#ffc657";
  } else if (highlightedValue > redLimit) {
    highlightedColour = "#ff7181";
  }

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("analytics_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <ButtonDropdown header={translateText("download_data", language)}>
                <>
                  <Menu.Item>
                    <CSVLink
                      className="bg-white text-black/70 hover:text-black hover:bg-light/20 text-xs block px-4 py-2"
                      data={analytics.export === null ? [] : analytics.export}
                      filename="audico_analytics_data_export.csv"
                    >
                      CSV
                    </CSVLink>
                  </Menu.Item>
                </>
              </ButtonDropdown>
            }
          />
        }
      />

      {/* Loading spinner */}
      {analytics.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              queryParams={[["dateRange", dateRangeTerm]]}
              handlers={{
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={analytics.loading}
              baseUrl="/insights/analytics"
              showDateRange={true}
              showFilters={false}
              showSearchBar={false}
            />
            {analyticsData !== null ? (
              <div className="mx-1 mb-10">
                {/* Time series */}
                <div className="grid grid-cols-1 mt-6">
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText(
                              "analytics_request_time_series",
                              language,
                            )}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              "analytics_request_time_series_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <LineChart
                          data={requestsByHour}
                          className="mx-auto text-sm lg:h-72 h-64"
                        />
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="grid md:grid-cols-3 grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Request category */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText(
                              "analytics_category_frequency",
                              language,
                            )}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              "analytics_category_frequency_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={categoryFrequency}
                          className="mx-auto text-sm lg:h-80 h-64"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Average response time */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("average_response_time", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              "analytics_average_response_time_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <RadialChart
                          data={responseTime}
                          showPlaceholders={
                            responseTime.length === 0
                              ? false
                              : responseTime.filter(
                                  (x) =>
                                    x.id ===
                                    translateText(
                                      "average_response_time",
                                      language,
                                    ),
                                )[0].data[0].y === 0
                          }
                          className="mx-auto h-56 text-sm sm:h-64 md:h-80 lg:h-96"
                          highlightedColour={highlightedColour}
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Request rooms */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText(
                              "analytics_room_frequency",
                              language,
                            )}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              "analytics_room_frequency_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={roomFrequency}
                          className="mx-auto text-sm lg:h-80 h-64"
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("not_enough_data", language)}
                subtext={translateText(
                  "not_enough_data_analytics_subtext",
                  language,
                )}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
